import { isObjectLike, map, isEmpty, isNull } from 'lodash';

import { LocaleService, ConfigsService, WindowProxy, UrlService } from '@sm360/widget-commons-core-services';
import {
    REDIRECTIONS,
    AccountService,
    EVENTS,
    AnalyticsService,
    SessionStorageService,
    ShiftDigitalService,
} from '@sm360/widget-commons-general';
import {
    PurchaseMethods,
    ModelYear,
    Quote,
    Vehicle,
    ShowroomService,
    DataOrigins,
} from '@sm360/widget-commons-vehicle';
import { LeadSubmissionBuilder, LEAD_TYPES } from '@sm360/widget-commons-leads';

import * as SECTIONS from 'build-and-quote/constants/sections.js';
import * as types from 'build-and-quote/actions/buildAndQuoteActionTypes.js';
import mapSectionToPageView from 'build-and-quote/constants/sectionToPageView.js';
import { BAD_QUOTE_REVISION } from 'build-and-quote/constants/errorCodes.js';

const { PurchaseMethodsActions } = PurchaseMethods;
const { ModelYearService, ModelYearActions } = ModelYear;
const { QuoteActions, QuoteService, LeadTypes, QuoteItemCategoryService } = Quote;
const { VehicleActions } = Vehicle;
const { MODEL_YEARS, MODEL_TRIMS, CONFIRMATION } = SECTIONS;

function dispatchAddToGarage() {
    const $window = WindowProxy.get();
    if ($window) {
        $window.document.dispatchEvent(new Event(EVENTS.GARAGE_ADDED));
    }
}

function setConfigurationToken(configurationToken) {
    return (dispatch, getState) => {
        const state = getState();
        const modelYear = ModelYearService.resetConfigurationToken(state.modelYear, configurationToken);
        const selectedOptionsPackage = modelYear.getSelectedOptionsPackage();

        if (selectedOptionsPackage) {
            dispatch(ModelYearActions.setOptionsPackages(modelYear));
        }
    };
}

function updateEmployeeRebate(quote, modelYear, selectedOptionsPackage) {
    return (dispatch) => {
        const employeeRebate = selectedOptionsPackage.configuration.getEmployeeRebate();
        const { employeeRebateAdjustment } = selectedOptionsPackage.configuration.purchaseMethods;

        if (employeeRebate) {
            const location = ConfigsService.getOrganizationUnitLocation().toLowerCase();
            const validationQuote = QuoteService.resetQuoteFromModelYear(quote, location, '', modelYear);
            dispatch(
                ShowroomService.saveQuote(validationQuote.getShowroomQuote(), true).then((showroomQuote) => {
                    const updatedEmployeeRebate = QuoteService.getEmployeeRebateFromShowroomQuote(showroomQuote);
                    if (
                        !updatedEmployeeRebate ||
                        updatedEmployeeRebate.rebate !== employeeRebate.rebate ||
                        employeeRebateAdjustment
                    ) {
                        const rebateAdjustment = !updatedEmployeeRebate
                            ? -1 * employeeRebate.rebate
                            : updatedEmployeeRebate.rebate - employeeRebate.rebate;

                        const newModelYear = ModelYearService.resetEmployeeRebateAdjustment(
                            modelYear,
                            rebateAdjustment
                        );
                        const newSelectedOptionsPackage = newModelYear.getSelectedOptionsPackage();

                        dispatch(ModelYearActions.setOptionsPackages(newModelYear));
                        dispatch(
                            PurchaseMethodsActions.loadPurchaseMethodsSuccess(
                                newSelectedOptionsPackage.configuration.purchaseMethods
                            )
                        );
                    }
                })
            );
        }
    };
}

function validateSelectedOptionsSuccess(options, skipResetModified, optionsAdjustmentDiscounts) {
    return (dispatch, getState) => {
        const state = getState();
        let modelYear = ModelYearService.resetOptionsAvailability(state.modelYear, options);
        modelYear = ModelYearService.resetEmployeeRebateAdjustment(modelYear, 0);
        modelYear = ModelYearService.resetPercentageRebateAdjustment(modelYear, optionsAdjustmentDiscounts);

        let selectedOptionsPackage = modelYear.getSelectedOptionsPackage();

        SessionStorageService.set('waitingValidation', JSON.stringify(false));
        if (selectedOptionsPackage) {
            const { purchaseMethods } = selectedOptionsPackage.configuration;
            const allSelectedOptions = purchaseMethods.getAllSelectedOptions();
            const optionsAdjustmentFCA = purchaseMethods.getOptionsAdjustmentFCA(allSelectedOptions);

            if (purchaseMethods.cashPurchase.optionsAdjustmentDiscounts > 0 && optionsAdjustmentFCA > 0) {
                const newOptionsAdjustmentDiscounts =
                    purchaseMethods.cashPurchase.optionsAdjustmentDiscounts +
                    optionsAdjustmentFCA *
                        (purchaseMethods.cashPurchase.optionsAdjustmentDiscounts /
                            (purchaseMethods.cashPurchase.optionsAdjustment + optionsAdjustmentFCA));

                const percentageRebateDifference =
                    newOptionsAdjustmentDiscounts - purchaseMethods.cashPurchase.optionsAdjustmentDiscounts;
                modelYear = ModelYearService.resetPercentageRebateAdjustment(
                    modelYear,
                    optionsAdjustmentDiscounts - percentageRebateDifference
                );
                selectedOptionsPackage = modelYear.getSelectedOptionsPackage();
                dispatch(ModelYearActions.setOptionsPackages(modelYear));
                dispatch(
                    PurchaseMethodsActions.loadPurchaseMethodsSuccess(
                        selectedOptionsPackage.configuration.purchaseMethods
                    )
                );
                dispatch(updateEmployeeRebate(state.quote, modelYear, selectedOptionsPackage));
            } else {
                dispatch(ModelYearActions.setOptionsPackages(modelYear));
                dispatch(
                    PurchaseMethodsActions.loadPurchaseMethodsSuccess(
                        selectedOptionsPackage.configuration.purchaseMethods
                    )
                );
                if (!skipResetModified) {
                    dispatch(QuoteActions.setQuoteModified());
                }
                dispatch(updateEmployeeRebate(state.quote, modelYear, selectedOptionsPackage));
            }
        }
    };
}

function validateSelectedOptionsFailure(optionSelectionResolutions) {
    return (dispatch, getState) => {
        SessionStorageService.set('waitingValidation', JSON.stringify(false));
        if (isObjectLike(optionSelectionResolutions) && !isEmpty(optionSelectionResolutions.resolutions)) {
            const state = getState();
            const modelYear = ModelYearService.resetOptionsSelectionResolutions(
                state.modelYear,
                optionSelectionResolutions
            );
            const selectedOptionsPackage = modelYear.getSelectedOptionsPackage();

            if (selectedOptionsPackage) {
                dispatch(ModelYearActions.setOptionsPackages(modelYear));
                dispatch(
                    PurchaseMethodsActions.loadPurchaseMethodsSuccess(
                        selectedOptionsPackage.configuration.purchaseMethods
                    )
                );
            }
        }
    };
}

function unselectInteriorColour() {
    return (dispatch, getState) => {
        const state = getState();
        const modelYear = ModelYearService.setSelectedInteriorColour(state.modelYear, null);
        dispatch(ModelYearActions.setOptionsPackages(modelYear));

        const interiorColourAmounts = {
            retail: 0,
            residualRetail: 0,
        };
        dispatch(PurchaseMethodsActions.setInteriorColourAmounts(interiorColourAmounts));
    };
}

function reloadAndSaveQuote(quote, dispatch) {
    ShowroomService.loadQuote(quote.id).then((upToDateQuote) => {
        const mergedQuote = quote.set('leadId', upToDateQuote.leadId).set('revision', upToDateQuote.revision);

        return ShowroomService.saveQuote(mergedQuote.getShowroomQuote()).then((showroomQuote) => {
            dispatchAddToGarage();
            dispatch(QuoteActions.setQuote(QuoteService.resetQuoteFromShowroomSave(mergedQuote, showroomQuote)));
        });
    });
}

export function validateSelectedOptions(
    modelYear,
    skipResetModified,
    optionSelectionCode,
    selectionResolutions,
    exteriorColourSelection
) {
    const { year, make } = modelYear;
    const configuration = modelYear.getSelectedOptionsPackageConfiguration();
    const locale = LocaleService.getLocale();
    SessionStorageService.set('waitingValidation', JSON.stringify(false));
    return (dispatch) => {
        if (
            SessionStorageService.get('waitingValidation') &&
            !JSON.parse(SessionStorageService.get('waitingValidation'))
        ) {
            SessionStorageService.set('waitingValidation', JSON.stringify(true));
            return ShowroomService.validateSelectedOptions(
                DataOrigins.OPTIONS_VALIDATION,
                year,
                make,
                configuration,
                locale,
                optionSelectionCode,
                selectionResolutions
            )
                .then(({ configurationToken, items, optionsAdjustmentDiscounts }) => {
                    dispatch(setConfigurationToken(configurationToken));
                    dispatch(validateSelectedOptionsSuccess(items, skipResetModified, optionsAdjustmentDiscounts));
                })
                .catch((error) => {
                    if (!exteriorColourSelection) {
                        dispatch(validateSelectedOptionsFailure(error));
                    } else {
                        dispatch(unselectInteriorColour());
                    }
                });
        }
        return true;
    };
}

function buildFullRedirectionUrl(showroomQuote, options, url) {
    const { id: quoteId } = showroomQuote;
    const queryParamsOptional = map(options, (value, key) => {
        return `${key}=${value}`;
    }).join('&');
    let fullUrl = `${url}?quoteId=${quoteId}`;
    if (queryParamsOptional) {
        fullUrl += `&${queryParamsOptional}`;
    }
    return fullUrl;
}

function saveQuoteAndRedirect(leadType, url, options) {
    return (dispatch, getState) => {
        let state = getState();
        const location = ConfigsService.getOrganizationUnitLocation().toLowerCase();
        dispatch(
            QuoteActions.setQuote(
                QuoteService.resetQuoteFromModelYear(state.quote, location, leadType, state.modelYear)
            )
        );

        state = getState();
        return ShowroomService.saveQuote(state.quote.getShowroomQuote())
            .then((showroomQuote) => {
                dispatch(QuoteActions.setQuote(QuoteService.resetQuoteFromShowroomSave(state.quote, showroomQuote)));
                state = getState();

                const windowProxy = WindowProxy.get();
                if (windowProxy) {
                    const historyUrlParams = `quoteId=${state.quote.id}&writeToken=${state.quote.securityToken}`;

                    windowProxy.history.replaceState(
                        {
                            quote: state.quote,
                        },
                        'buildAndQuote',
                        `${windowProxy.location.href}&${historyUrlParams}`
                    );

                    const fullUrl = buildFullRedirectionUrl(state.quote, options, url);
                    if (ConfigsService.isInMobileApp() && !AccountService.isAuthenticated()) {
                        windowProxy.location.href =
                            `ca.solutionsmedias360.showroom-${ConfigsService.getWebsiteId()}://` +
                            `?action=login&redirectUrl=${encodeURIComponent(fullUrl)}`;
                    } else {
                        windowProxy.location.href = fullUrl;
                    }
                }
            })
            .catch((error) => {
                throw error;
            });
    };
}

function getUrls() {
    const locale = LocaleService.getLocale();
    const langLocale = locale.includes('-') ? locale.split('-')[0] : locale;
    return REDIRECTIONS[langLocale];
}

function updateOptionsAmounts(skipOptionsValidation, option) {
    return (dispatch, getState) => {
        const state = getState();
        const configuration = state.modelYear.getSelectedOptionsPackageConfiguration();
        if (configuration) {
            dispatch(PurchaseMethodsActions.loadPurchaseMethodsSuccess(configuration.purchaseMethods));

            if (!skipOptionsValidation) {
                dispatch(
                    validateSelectedOptions(state.modelYear, skipOptionsValidation, option ? option.selectionCode : '')
                );
            }
        }
    };
}

function reportAnalyticsPageViewForSection(sectionId, model) {
    const pageView = mapSectionToPageView[sectionId];
    AnalyticsService.virtualPageView(pageView(model));
}

function withPageView(decoratedDispatcher) {
    return (toSectionId) => {
        return (dispatch, getState) => {
            const currentSectionId = getState().buildAndQuote.buildAndQuoteSection;
            if (currentSectionId !== toSectionId) {
                const { model, year } = getState().modelYear;
                reportAnalyticsPageViewForSection(toSectionId, `${model} ${year}`);
            }
            dispatch(decoratedDispatcher(toSectionId));
        };
    };
}

export function loadModelYearsSuccess(modelYears) {
    return (dispatch, getState) => {
        const state = getState();
        const modelYear = ModelYearService.resetModelYears(state.modelYear, modelYears);
        const selectedYearPurchaseMethods = modelYear.getSelectedYearPurchaseMethods();
        if (selectedYearPurchaseMethods) {
            dispatch(PurchaseMethodsActions.loadPurchaseMethodsSuccess(selectedYearPurchaseMethods));
        }
        dispatch(ModelYearActions.loadModelYearSuccess(modelYear));
    };
}

export function selectModelYear(year) {
    return (dispatch, getState) => {
        const state = getState();
        const modelYear = ModelYearService.setSelectedModelYear(state.modelYear, year);
        dispatch(ModelYearActions.setModelYears(modelYear));
        dispatch(PurchaseMethodsActions.loadPurchaseMethodsSuccess(modelYear.getSelectedYear().purchaseMethods));
        dispatch(QuoteActions.setQuoteModified());
    };
}

export function selectModelTrim(modelTrim, skipOptionsValidation) {
    return (dispatch, getState) => {
        const state = getState();
        const modelYear = ModelYearService.setSelectedModelTrim(state.modelYear, modelTrim);
        const selectedOptionsPackage = modelYear.getSelectedOptionsPackage();

        if (selectedOptionsPackage) {
            dispatch(ModelYearActions.setModelTrims(modelYear));
            if (!skipOptionsValidation) {
                dispatch(ModelYearActions.loadOptions(modelYear)).then((modelYearWithOptions) => {
                    dispatch(
                        ModelYearActions.updateSelectedPackageAmounts(
                            selectedOptionsPackage,
                            false,
                            modelYearWithOptions
                        )
                    );
                });
            } else {
                dispatch(
                    ModelYearActions.updateSelectedPackageAmounts(
                        selectedOptionsPackage,
                        skipOptionsValidation,
                        modelYear
                    )
                );
            }
        }
    };
}

export function selectBoxSize(boxSize, skipOptionsValidation) {
    return (dispatch, getState) => {
        let state = getState();
        const selectedModelTrim = state.modelYear.getSelectedModelTrim();
        const modelYear = ModelYearService.setSelectedBoxSize(state.modelYear, boxSize);
        const updatedSelectedModelTrim = modelYear.modelTrims
            .filter((modelTrim) => modelTrim.selectionCode === selectedModelTrim.selectionCode)
            .first();

        if (updatedSelectedModelTrim.visible && updatedSelectedModelTrim.enabled) {
            dispatch(ModelYearActions.setModelYear(modelYear));
            dispatch(selectModelTrim(updatedSelectedModelTrim, skipOptionsValidation));
        } else {
            const selectedOptionsPackage = modelYear.getSelectedOptionsPackage();

            if (selectedOptionsPackage) {
                dispatch(ModelYearActions.setModelYear(modelYear));
                state = getState();
                dispatch(ModelYearActions.loadOptions(state.modelYear)).then(() => {
                    dispatch(
                        ModelYearActions.updateSelectedPackageAmounts(selectedOptionsPackage, skipOptionsValidation)
                    );
                });
            }
        }
    };
}

export function selectCabSize(cabSize, skipOptionsValidation) {
    return (dispatch, getState) => {
        let state = getState();
        const selectedModelTrim = state.modelYear.getSelectedModelTrim();
        const modelYear = ModelYearService.setSelectedCabSize(state.modelYear, cabSize);
        const updatedSelectedModelTrim = modelYear.modelTrims
            .filter((modelTrim) => modelTrim.selectionCode === selectedModelTrim.selectionCode)
            .first();

        if (updatedSelectedModelTrim.visible && updatedSelectedModelTrim.enabled) {
            dispatch(ModelYearActions.setModelYear(modelYear));
            dispatch(selectModelTrim(updatedSelectedModelTrim, skipOptionsValidation));
        } else {
            const selectedOptionsPackage = modelYear.getSelectedOptionsPackage();

            if (selectedOptionsPackage) {
                dispatch(ModelYearActions.setModelYear(modelYear));
                if (!skipOptionsValidation) {
                    state = getState();
                    dispatch(ModelYearActions.loadOptions(state.modelYear)).then(() => {
                        dispatch(
                            ModelYearActions.updateSelectedPackageAmounts(selectedOptionsPackage, skipOptionsValidation)
                        );
                    });
                } else {
                    dispatch(
                        ModelYearActions.updateSelectedPackageAmounts(selectedOptionsPackage, skipOptionsValidation)
                    );
                }
            }
        }
    };
}

export function selectModelTrimFromCode(modelTrimCode, skipOptionsValidation) {
    return (dispatch, getState) => {
        const state = getState();
        const newOptionsPackage = state.modelYear.optionsPackages
            .filter((optionsPackage) => optionsPackage.configuration.modelTrimCode === modelTrimCode)
            .first();
        const modelTrimSelectionCode = newOptionsPackage ? newOptionsPackage.configuration.modelTrim.selectionCode : '';
        const newModelTrim = modelTrimSelectionCode
            ? state.modelYear.modelTrims
                  .filter((modelTrim) => modelTrim.selectionCode === modelTrimSelectionCode)
                  .first()
            : state.modelYear.modelTrims.first();

        dispatch(selectModelTrim(newModelTrim, skipOptionsValidation));
    };
}

export function selectDrivePowerTrain(drivePowerTrain) {
    return (dispatch, getState) => {
        let state = getState();
        const modelYear = ModelYearService.setSelectedDrivePowerTrain(state.modelYear, drivePowerTrain);
        const selectedOptionsPackage = modelYear.getSelectedOptionsPackage();

        if (selectedOptionsPackage) {
            dispatch(ModelYearActions.setDrivePowerTrains(modelYear));
            state = getState();
            dispatch(ModelYearActions.loadOptions(state.modelYear)).then(() => {
                dispatch(ModelYearActions.updateSelectedPackageAmounts(selectedOptionsPackage, false));
            });
        }
    };
}

export function selectOptionsPackage(optionsPackage) {
    return (dispatch, getState) => {
        let state = getState();
        const modelYear = ModelYearService.setSelectedOptionsPackage(state.modelYear, optionsPackage);
        const selectedOptionsPackage = modelYear.getSelectedOptionsPackage();

        if (selectedOptionsPackage) {
            dispatch(ModelYearActions.setOptionsPackages(modelYear));
            state = getState();
            dispatch(ModelYearActions.loadOptions(state.modelYear)).then(() => {
                dispatch(ModelYearActions.updateSelectedPackageAmounts(selectedOptionsPackage, false));
            });
        }
    };
}

export function selectExteriorColour(exteriorColour) {
    return (dispatch, getState) => {
        const state = getState();
        const modelYear = ModelYearService.setSelectedExteriorColour(state.modelYear, exteriorColour);
        dispatch(ModelYearActions.setOptionsPackages(modelYear));
        dispatch(ModelYearActions.updateExteriorColourAmounts(false));
    };
}

export function selectInteriorColour(interiorColour) {
    return (dispatch, getState) => {
        const state = getState();
        const modelYear = ModelYearService.setSelectedInteriorColour(state.modelYear, interiorColour);
        dispatch(ModelYearActions.setOptionsPackages(modelYear));
        dispatch(ModelYearActions.updateInteriorColourAmounts(false));
    };
}

export function setBuildAndQuoteSection(buildAndQuoteSection) {
    return { type: types.CHANGE_BUILD_AND_QUOTE_SECTION, buildAndQuoteSection };
}

export function loadQuote(quoteId) {
    return (dispatch) => {
        return (
            ShowroomService.loadQuote(quoteId)
                .then((quote) => {
                    dispatch(QuoteActions.setQuote(quote.set('modified', false)));
                    const windowProxy = WindowProxy.get();
                    let securityToken;
                    if (windowProxy && windowProxy.localStorage) {
                        securityToken = windowProxy.localStorage.getItem('securityToken');
                    }
                    if (securityToken) {
                        dispatch(QuoteActions.setQuoteSecurityToken(securityToken));
                    }
                })
                // TODO : Ce serait plus idiomatique de lancer une actions qui permettrais au reducer de gérer l'erreur.
                .catch((error) => {
                    throw error;
                })
        );
    };
}

export function loadModelYears(modelYearsParams, tradeInVehicleParams, initialLoad) {
    return (dispatch) => {
        dispatch(ModelYearActions.loadModelYears(modelYearsParams, tradeInVehicleParams, initialLoad)).then(() => {
            dispatch(setBuildAndQuoteSection(MODEL_YEARS.value));
        });
    };
}

export function loadModelYear(modelYearsParams, tradeInVehicleParams, quoteId, unfiltered = false) {
    return (dispatch, getState) => {
        dispatch(ModelYearActions.loadModelYear(modelYearsParams, tradeInVehicleParams, unfiltered)).then(() => {
            if (!quoteId) {
                dispatch(withPageView(setBuildAndQuoteSection)(MODEL_TRIMS.value));

                const state = getState();
                const exteriorColour = state.modelYear.getSelectedOptionsPackageExteriorColour();
                if (exteriorColour) {
                    dispatch(ModelYearActions.loadOptions(state.modelYear)).then(() => {
                        dispatch(selectExteriorColour(exteriorColour));
                    });
                } else {
                    dispatch(ModelYearActions.loadOptions(state.modelYear));
                }
            } else {
                dispatch(ModelYearActions.updateFromQuote(quoteId, CONFIRMATION.value)).then(() => {
                    dispatch(withPageView(setBuildAndQuoteSection)(CONFIRMATION.value));
                    const state = getState();
                    dispatch(validateSelectedOptions(state.modelYear, false, ''));
                    setTimeout(() => {
                        dispatch(validateSelectedOptions(state.modelYear, false, ''));
                    }, 1000);
                });
            }
        });
    };
}

export function changeBuildAndQuoteSection(buildAndQuoteSection) {
    return (dispatch, getState) => {
        const state = getState();
        let { modelYear } = state;
        const currentPurchaseMethods = state.purchaseMethods;
        const selectedPurchaseMethod = currentPurchaseMethods.getSelectedPurchaseMethod();
        const selectedPurchaseMethodValue = currentPurchaseMethods.getSelectedPurchaseMethodValue();

        switch (buildAndQuoteSection) {
            case MODEL_YEARS.value:
                if (!modelYear.modelYearsAreLoaded()) {
                    dispatch(loadModelYears(modelYear, { tradeInVehicleId: state.tradeInVehicle.id }));
                } else {
                    modelYear = ModelYearService.resetPaymentsAndPurchaseAmounts(
                        ModelYearService.resetModelYear(modelYear),
                        currentPurchaseMethods
                    );
                    modelYear = ModelYearService.selectPurchaseMethod(modelYear, selectedPurchaseMethod);
                    dispatch(ModelYearActions.setModelYear(modelYear));
                    dispatch(
                        PurchaseMethodsActions.loadPurchaseMethodsSuccess(modelYear.getSelectedYearPurchaseMethods())
                    );
                }
                break;
            case MODEL_TRIMS.value: {
                const isFromYears = state.buildAndQuote.buildAndQuoteSection === MODEL_YEARS.value;

                if (isFromYears) {
                    const selectedYear = modelYear.getSelectedYear();
                    const modelTrimId = selectedYear.getModelTrimSelectedPurchaseMethodId(selectedPurchaseMethodValue);

                    if (!modelYear.modelYearIsLoaded()) {
                        const modelYearsParams = {
                            year: selectedYear.year,
                            make: modelYear.make,
                            model: modelYear.model,
                            bodyStyleCode: modelYear.bodyStyleCode,
                            modelTrimCode: modelTrimId ? modelTrimId.modelTrimCode : '',
                        };
                        const tradeInVehicleParams = {
                            id: state.tradeInVehicle.id,
                        };
                        dispatch(loadModelYear(modelYearsParams, tradeInVehicleParams));
                    } else {
                        modelYear = ModelYearService.resetModelYear(modelYear, selectedYear);
                        modelYear = ModelYearService.resetPaymentsAndPurchaseAmounts(modelYear, currentPurchaseMethods);
                        modelYear = ModelYearService.selectPurchaseMethod(modelYear, selectedPurchaseMethod);

                        const { cabSizes } = modelYear;
                        const selectedCabSize = modelYear.getSelectedCabSize();
                        if (cabSizes.length > 0) {
                            modelYear = ModelYearService.setSelectedCabSize(modelYear, selectedCabSize || cabSizes[0]);
                        }

                        dispatch(ModelYearActions.setModelYear(modelYear));
                        dispatch(
                            PurchaseMethodsActions.loadPurchaseMethodsSuccess(
                                modelYear.getSelectedOptionsPackagePurchaseMethods()
                            )
                        );

                        if (modelTrimId) {
                            dispatch(selectModelTrimFromCode(modelTrimId.modelTrimCode, false));
                        }
                    }
                }
                break;
            }
            case CONFIRMATION.value: {
                const location = ConfigsService.getOrganizationUnitLocation().toLowerCase();
                const newQuote = QuoteService.resetQuoteFromModelYear(
                    state.quote,
                    location,
                    LeadTypes.APPOINTMENT,
                    state.modelYear
                );
                dispatch(QuoteActions.setQuote(newQuote));
                break;
            }
            default:
                break;
        }
        dispatch(withPageView(setBuildAndQuoteSection)(buildAndQuoteSection));
    };
}

export function setMobilePaymentCalculatorOpened(mobilePaymentCalculatorOpened) {
    return { type: types.SET_MOBILE_PAYMENT_CALCULATOR_OPENED, mobilePaymentCalculatorOpened };
}

export function setQuote() {
    return (dispatch, getState) => {
        const location = ConfigsService.getOrganizationUnitLocation().toLowerCase();
        const state = getState();
        dispatch(
            QuoteActions.setQuote(QuoteService.resetQuoteFromModelYear(state.quote, location, '', state.modelYear))
        );
    };
}

export function saveToGarage(newAccount) {
    return (dispatch, getState) => {
        const location = ConfigsService.getOrganizationUnitLocation().toLowerCase();

        let state = getState();

        const { quote } = state;

        dispatch(
            QuoteActions.setQuote(
                QuoteService.resetQuoteFromModelYear(quote, location, LeadTypes.SAVE_TO_GARAGE, state.modelYear)
            )
        );

        state = getState();

        if (AccountService.isAuthenticated()) {
            return ShowroomService.saveQuote(quote.getShowroomQuote())
                .then((showroomQuote) => {
                    const mappedQuote = QuoteService.resetQuoteFromShowroomSave(quote, showroomQuote);

                    dispatchAddToGarage();

                    if (QuoteService.quoteHasVehicle(mappedQuote)) {
                        const leadRequest = new LeadSubmissionBuilder({}, 'Add To Garage')
                            .addLeadType(LEAD_TYPES.ACCOUNT_CREATION)
                            .addQuote(mappedQuote)
                            .addVehicle(mappedQuote);

                        if (newAccount) {
                            leadRequest.setIsAccountCreation();
                            leadRequest.addOptInEmail(newAccount.optInEmail);
                            leadRequest.addContactInformation(
                                newAccount.firstname,
                                newAccount.lastname,
                                newAccount.homePhone,
                                newAccount.email1,
                                newAccount.contactId
                            );
                        }

                        leadRequest.submit();

                        setTimeout(
                            () =>
                                ShowroomService.loadQuote(mappedQuote.id).then((reloadedQuote) => {
                                    ShiftDigitalService.updateDrOfferAdded(
                                        reloadedQuote.id,
                                        reloadedQuote.vehicle,
                                        reloadedQuote.vehicle.purchaseMethods.getSelectedPurchaseMethod()
                                    );
                                    return dispatch(QuoteActions.setQuote(reloadedQuote));
                                }),
                            2000
                        );
                    } else {
                        dispatch(QuoteActions.setQuote(mappedQuote));
                    }

                    return true;
                })
                .catch((error) => {
                    if (
                        error &&
                        error.response &&
                        error.response.data &&
                        error.response.data.error === BAD_QUOTE_REVISION
                    ) {
                        reloadAndSaveQuote(quote, dispatch);
                    } else {
                        throw error;
                    }
                });
        }

        return true;
    };
}
export function setVehicle(vehicle) {
    return (dispatch) => {
        dispatch(VehicleActions.setVehicle(vehicle));
        dispatch(QuoteActions.setQuoteVehicle(vehicle));
    };
}
export function saveToGarageV25(newAccount, config) {
    return (dispatch, getState) => {
        const location = ConfigsService.getOrganizationUnitLocation();
        const state = getState();

        // Ensure the vehicle is stored in the quote
        dispatch(
            QuoteActions.setQuoteV25(QuoteService.resetQuoteFromVehicleV25(state.quoteV25, location, state.vehicleV25))
        );

        const addToGarageV25 = new Quote.AddToGarageV25();

        dispatch(QuoteActions.setQuoteLoading(true));
        addToGarageV25
            .addOrUpdateQuote(state.quoteV25, newAccount, config)
            .then((quoteId) => {
                UrlService.addOrReplaceParameter('quoteId', quoteId);
                let tries = 0;
                const interval = setInterval(
                    () =>
                        ShowroomService.searchQuoteV25(quoteId)
                            .then((updatedQuote) => {
                                ShiftDigitalService.updateDrOfferAdded(
                                    updatedQuote.id,
                                    updatedQuote.vehicle,
                                    updatedQuote.vehicle.purchaseMethods.getSelectedPurchaseMethodValue()
                                );
                                if (updatedQuote.leadId) {
                                    dispatch(QuoteActions.setQuoteV25(updatedQuote));
                                    addToGarageV25.dispatchAddToGarage();
                                    return updatedQuote.leadId;
                                }
                                return undefined;
                            })
                            .then((leadId) => {
                                if (tries > 5 || leadId) clearInterval(interval);
                                tries += 1;
                            }),
                    2000
                );
            })
            .catch((error) => {
                dispatch(QuoteActions.setQuoteError(error?.message));
            });
    };
}

export function buyOnline(leadType) {
    const checkoutUrl = getUrls().checkout;
    return saveQuoteAndRedirect(leadType, checkoutUrl);
}

export function prepareAppointment() {
    const appointmentUrl = getUrls().appointment;
    const options = {
        leadType: LeadTypes.APPOINTMENT,
    };
    return saveQuoteAndRedirect(LeadTypes.APPOINTMENT, appointmentUrl, options);
}

export function selectOption(optionCategory, option) {
    return (dispatch, getState) => {
        const state = getState();
        const selectedConfiguration = state.modelYear.getSelectedOptionsPackageConfiguration();

        if (selectedConfiguration) {
            const options = QuoteItemCategoryService.selectQuoteItemPackage(
                selectedConfiguration.options,
                optionCategory,
                option
            );
            const modelYear = ModelYearService.setSelectedOptions(state.modelYear, options);
            dispatch(ModelYearActions.setOptionsPackages(modelYear));
            dispatch(updateOptionsAmounts(false, option));
        }
    };
}

export function applyOptionSelectionResolution() {
    return (dispatch, getState) => {
        const state = getState();
        const { modelYear, purchaseMethods } = state;

        const { optionsSelectionResolutions } = purchaseMethods;
        if (!isNull(optionsSelectionResolutions)) {
            const optionCode = optionsSelectionResolutions.resolutionTriggerOptionSelectionCode;
            dispatch(validateSelectedOptions(modelYear, false, optionCode, optionsSelectionResolutions));
        }
    };
}
